import React, { useState } from 'react';
import { panelGet } from '../../kit';
import { fmtN } from '../../monthly';
import { Badge, Check } from '../emailKit';
import { saveBlob } from '../../ipintel/shared';
import { apiService } from '../../../../services/apiService';

/**
 * What the Email Center → Dashboard views share (backend:
 * Backend/admin_email_stats.py, section "email": stats_overview, stats_weekly,
 * stats_campaigns, stats_sends, stats_export).
 *
 * Conventions the numbers follow (owner decisions for v1, 2026-10-11):
 *  - a week starts on Monday, India time (Asia/Kolkata), and so do the
 *    period's days — the server cuts both;
 *  - open rate = (opened or clicked) / delivered, click rate = clicked /
 *    delivered, CTOR = clicked / opened, all 0–1 fractions;
 *  - open rate (and CTOR) is null for a plain-text template — SendGrid cannot
 *    see a plain-text mail being opened — shown as "n/a", never as 0 %;
 *  - opened / clicked are a person's by default (owner, 2026-10-25;
 *    Backend/email_engagement.py): instant reads, Apple MPP prefetches and
 *    scanner opens are "auto opens", scanner clicks "bot clicks", both counted
 *    apart (auto_opened / bot_clicked); automated=1 ("Include automated")
 *    brings back the raw SendGrid numbers in the same fields.
 *
 * The server is new alongside this tab, so every read here is defensive: a
 * missing key reads as nothing rather than breaking the page, and a rate the
 * server left out is worked out from the counts it did send.
 */

export const SECTION = 'email';

// ---------------------------------------------------------------------------
// Numbers
// ---------------------------------------------------------------------------
export const num = (v: any): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
export const str = (v: any): string => (v === null || v === undefined ? '' : String(v));
export const arr = (v: any): any[] => (Array.isArray(v) ? v : []);

const RATE_PARTS: Record<string, [string, string]> = {
  open_rate: ['opened', 'delivered'], click_rate: ['clicked', 'delivered'],
  ctor: ['clicked', 'opened'], bounce_rate: ['bounced', 'sent'],
};

/** A rate off a row: the server's value (null stays null = "n/a"), else
 *  worked out from the counts, else undefined (nothing to show). */
export const rateOf = (row: any, key: string): number | null | undefined => {
  if (!row) return undefined;
  // A row the server marked plain text has no opens, whatever it sent.
  if ((key === 'open_rate' || key === 'ctor') && row.html === false && !(key in row)) return null;
  if (key in row) {
    const v = row[key];
    if (v === null) {
      // The server sends null for "nothing to divide by" as well (nothing
      // delivered yet, an empty week): that is "—", not the plain-text "n/a".
      const parts = RATE_PARTS[key];
      if (parts && parts[1] in row && !num(row[parts[1]])) return undefined;
      return null;
    }
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }
  const parts = RATE_PARTS[key];
  if (!parts) return undefined;
  const d = num(row[parts[1]]);
  return d ? num(row[parts[0]]) / d : undefined;
};

/** 0–1 fraction → "34%" / "3.4%"; null → "n/a"; undefined → "—". */
export const pctText = (v: number | null | undefined): string => {
  if (v === null) return 'n/a';
  if (v === undefined || !Number.isFinite(v)) return '—';
  const p = v * 100;
  return `${p < 10 && p > 0 ? p.toFixed(1) : Math.round(p)}%`;
};

export const NA_TITLE = 'Plain-text e-mail: SendGrid cannot see it being opened, so there is no open rate.';

/** A rate cell: "n/a" (with the reason on hover) for a plain-text template. */
export const Pct: React.FC<{ v: number | null | undefined; strong?: boolean }> = ({ v, strong }) => (
  v === null
    ? <span className="text-grayscale-1100 cursor-help" title={NA_TITLE}>n/a</span>
    : <span className={strong ? 'font-medium' : ''}>{pctText(v)}</span>
);

export const N: React.FC<{ v: any }> = ({ v }) =>
  <>{v === null || v === undefined || v === '' ? '—' : fmtN(num(v))}</>;

// ---------------------------------------------------------------------------
// Dates (India time; the server buckets weeks the same way)
// ---------------------------------------------------------------------------
const IST_MS = 330 * 60 * 1000;
export const istToday = (): string => new Date(Date.now() + IST_MS).toISOString().slice(0, 10);
const addDays = (iso: string, n: number): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
/** The Monday on or before a day. */
export const mondayOf = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  if (isNaN(d.getTime())) return iso;
  return addDays(iso, -((d.getUTCDay() + 6) % 7));
};
export const weekEnd = (week: string): string => addDays(week, 6);

const dm = (iso: string, withYear = false) => {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  return isNaN(d.getTime()) ? iso
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC' });
};
/** "w/c 6 Oct" (with the year when it is not this year). */
export const weekLabel = (week: string): string => {
  const y = week.slice(0, 4);
  return `w/c ${dm(week, y !== istToday().slice(0, 4))}`;
};
export const dayText = (iso: string) => (iso ? dm(iso, true) : '');

/** Period presets. Week presets start on a Monday, so the first week in the
 *  table is a whole one; the running week is always partial. */
export const WINDOWS = ['4w', '12w', '26w', '52w', '30d', '90d', 'custom'];
export const WINDOW_LABELS: Record<string, string> = {
  '4w': 'Last 4 weeks', '12w': 'Last 12 weeks', '26w': 'Last 26 weeks', '52w': 'Last 52 weeks',
  '30d': 'Last 30 days', '90d': 'Last 90 days', custom: 'Custom range…',
};
export const WINDOW_DAYS: Record<string, number | null> = {
  '4w': 28, '12w': 84, '26w': 182, '52w': 364, '30d': 30, '90d': 90,
};

/** A preset (or the picker's custom days) as the from / to the server takes. */
export const rangeOf = (p: Record<string, string>): { from?: string; to?: string } => {
  const w = p.window || '12w';
  if (w === 'custom') {
    const out: { from?: string; to?: string } = {};
    if (p.from) out.from = p.from;
    if (p.to) out.to = p.to;
    return out;
  }
  const today = istToday();
  const m = /^(\d+)([wd])$/.exec(w);
  if (!m) return { from: addDays(today, -83), to: today };
  const n = Number(m[1]);
  return m[2] === 'w'
    ? { from: addDays(mondayOf(today), -7 * (n - 1)), to: today }
    : { from: addDays(today, -(n - 1)), to: today };
};

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------
export const MODES = ['all', 'manual', 'auto'];
export const MODE_LABELS: Record<string, string> = {
  all: 'Manual and automatic', manual: 'Manual only', auto: 'Automatic only',
};
const MODE_WORD: Record<string, string> = {
  manual: 'Manual', auto: 'Automatic', admin_action: 'Admin action', test: 'Test', mixed: 'Mixed',
};
const MODE_HINT: Record<string, string> = {
  manual: 'An admin pressed Send (a campaign, a MEMAC send, outreach)',
  auto: 'Sent by the system on its own (sign-up, trial, billing, MEMAC auto)',
  admin_action: 'Sent because an admin did something (granted a plan, changed a plan)',
  mixed: 'Some sends of this template were manual and some automatic',
};
export const modeWord = (m: any) => MODE_WORD[str(m)] || str(m) || '—';
export const ModePill: React.FC<{ mode: any }> = ({ mode }) => {
  const m = str(mode);
  if (!m) return <span className="text-grayscale-1100">—</span>;
  const tone = m === 'manual' ? 'blue' : m === 'auto' ? 'green' : m === 'test' ? 'grey' : 'amber';
  return <span title={MODE_HINT[m]}><Badge tone={tone}>{modeWord(m)}</Badge></span>;
};

/** MEMAC's categories first (gaps §4.2), then whatever else the server sends. */
export const KNOWN_CATEGORIES = ['user_activity', 'new_data', 'features', 'marketing', 'industry_news', 'billing', 'service'];
const CATEGORY_WORD: Record<string, string> = {
  user_activity: 'User activity', new_data: 'New data', features: 'New features', marketing: 'Marketing',
  industry_news: 'Industry news', billing: 'Billing', service: 'Service', campaign: 'Campaign',
  outreach: 'Researcher outreach', transactional: 'Transactional', sign_in: 'Sign-in', other: 'Other',
};
/** The server's own category labels (stats_overview → by_category[].label),
 *  learnt as they arrive, ahead of the built-in words. */
const serverCategoryWord: Record<string, string> = {};
export const learnCategoryLabels = (rows: any[]) => {
  arr(rows).forEach(r => { const k = str(r?.category); if (k && str(r?.label)) serverCategoryWord[k] = str(r.label); });
};
export const categoryWord = (c: any): string => {
  const k = str(c);
  if (!k) return 'Uncategorised';
  return serverCategoryWord[k] || CATEGORY_WORD[k] || k.replace(/_/g, ' ').replace(/^\w/, x => x.toUpperCase());
};

export const SOURCES = ['campaign', 'memac', 'auto', 'outreach', 'invite', 'event'];
export const SOURCE_WORD: Record<string, string> = {
  campaign: 'Campaign', auto: 'Automatic', memac: 'MEMAC', outreach: 'Researcher outreach',
  invite: 'Invites', event: 'SendGrid events only',
};
export const SOURCE_HINT: Record<string, string> = {
  campaign: 'A campaign sent from Email Center → Campaigns (exact)',
  auto: 'An automatic mail recorded in the send ledger (exact)',
  memac: 'Sent by a MEMAC trigger (exact)',
  outreach: 'Researcher outreach, manual send or mail-merge (exact)',
  invite: 'Invitation e-mails from bulk add and Send invites',
  event: 'Known only from SendGrid events — counted when SendGrid reported something (approximate)',
};

/** The primary CTA a row carries, whichever shape the server used.
 *  `trackable` false = SendGrid cannot count its clicks (a mailto: button). */
export const ctaOf = (r: any): { label: string; url: string; trackable: boolean } => {
  const c = r?.primary_cta;
  const o = c && typeof c === 'object'
    ? { label: str(c.label), url: str(c.url), t: c.trackable }
    : { label: str(c ?? r?.primary_cta_label), url: str(r?.primary_cta_url), t: undefined };
  const trackable = o.t === undefined || o.t === null ? !/^mailto:/i.test(o.url) : !!o.t;
  return { label: o.label, url: o.url, trackable };
};
export const CtaCell: React.FC<{ row: any }> = ({ row }) => {
  const { label, url, trackable } = ctaOf(row);
  if (!label && !url) return <span className="text-grayscale-1100" title="This e-mail has no button">—</span>;
  return (
    <span className="inline-flex items-center gap-1" title={url || undefined}>
      <span className="truncate max-w-[12rem]">{label || url}</span>
      {!trackable && (
        <span title={/^mailto:/i.test(url) ? 'A mailto: button — SendGrid cannot count its clicks'
          : 'SendGrid cannot count clicks on this button'}>
          <Badge tone="amber">{/^mailto:/i.test(url) ? 'mailto' : 'not countable'}</Badge>
        </span>
      )}
    </span>
  );
};
/** CTA clicks: "n/a" when the button cannot be counted (the server sends
 *  null), "—" when the mail has no button. */
export const CtaClicks: React.FC<{ row: any }> = ({ row }) => {
  const { label, url, trackable } = ctaOf(row);
  if (!label && !url) return <span className="text-grayscale-1100">—</span>;
  if (!trackable || row?.cta_clicks === null) {
    return <span className="text-grayscale-1100 cursor-help" title="This button's clicks cannot be counted (mailto:)">n/a</span>;
  }
  return <N v={row?.cta_clicks} />;
};

// ---------------------------------------------------------------------------
// Automated engagement (Backend/email_engagement.py): the tags a send or a
// recipient carries, the "Include automated" switch, the definitions note
// ---------------------------------------------------------------------------
export type EngTag = { key: string; label: string; n: number; reason: string };
const ENG_LABEL: Record<string, string> = {
  instant_open: 'Instant read (auto)', machine_open: 'Apple MPP', scanner_open: 'Scanner open (auto)',
  bot_click: 'Bot click',
};
const ENG_TONE: Record<string, 'amber' | 'grey' | 'red'> = {
  instant_open: 'amber', machine_open: 'grey', scanner_open: 'amber', bot_click: 'red',
};
/** A row's automated-engagement tags (eng_tags: {key, label, n, reason}). */
export const engTags = (r: any): EngTag[] => arr(r?.eng_tags)
  .filter((t: any) => t && str(t.key))
  .map((t: any) => ({ key: str(t.key), label: str(t.label) || ENG_LABEL[str(t.key)] || str(t.key),
    n: num(t.n), reason: str(t.reason) }));
/** "Instant read (auto)", "Apple MPP", "Bot click"… with the reason (user
 *  agent, network, seconds after delivery) on hover. */
export const EngTags: React.FC<{ row: any }> = ({ row }) => {
  const tags = engTags(row);
  if (!tags.length) return null;
  return (
    <>
      {tags.map(t => (
        <span key={t.key} title={t.reason || t.label} className="cursor-help">
          <Badge tone={ENG_TONE[t.key] || 'grey'}>{t.label}{t.n > 1 ? ` ×${t.n}` : ''}</Badge>
        </span>
      ))}
    </>
  );
};

/** The switch every engagement view shares: off = people's opens and clicks
 *  (the default), on = every open and click SendGrid reported. */
export const AutomatedToggle: React.FC<{ value: boolean; onChange: (v: boolean) => void }> = ({ value, onChange }) => (
  <span title="Off: opens and clicks by people only (the default). On: the raw SendGrid numbers, with instant reads, Apple MPP prefetches, scanner opens and bot clicks counted too.">
    <Check checked={value} onChange={onChange}>Include automated</Check>
  </span>
);

const ENG_DEFAULT_DEFS = [
  'Human opens are what the rates count by default. An open is automated when Apple Mail Privacy Protection '
  + 'fetched it, when it came within 60 seconds of delivery (an instant read: a mailbox or security scanner '
  + 'prefetching the mail), or when a known scanner made it (Google\'s image scanner, Microsoft Defender, scripts).',
  'A click is a bot click when a scanner made it (its browser or network: Microsoft Defender, Gmail\'s link check, '
  + 'python-requests, Internet Explorer), when it came within 60 seconds of delivery, or when several different '
  + 'links were clicked within 2 seconds. A click on an instant read is not assumed human: most of those are link scanners.',
  'A mail counts as opened when a person opened it or made a human click. A person\'s open after an instant '
  + 'prefetch still counts.',
  '"Include automated" shows the raw numbers SendGrid reports, every open and click counted.',
];

/** The small info note: what is a person's open, what is automated. `eng`
 *  is the server's engagement block ({instant_sec, definitions}); the
 *  built-in words stand in for an older server. */
export const EngagementNote: React.FC<{ eng?: any; automated: boolean; className?: string }> = ({ eng, automated, className }) => {
  const defs = arr(eng?.definitions).map(str).filter(Boolean);
  const sec = num(eng?.instant_sec) || 60;
  return (
    <details className={`text-xs text-grayscale-1200 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 ${className || ''}`}>
      <summary className="cursor-pointer">
        <span aria-hidden className="mr-1">ⓘ</span>
        {automated
          ? <>Showing <b>every</b> open and click SendGrid reported, automated ones included.</>
          : <>Opens and clicks are <b>people's</b>. Instant reads (within {sec} s of delivery), Apple MPP prefetches,
            scanner opens and bot clicks are counted apart.</>}
        {' '}<span className="underline underline-offset-2">How is this decided?</span>
      </summary>
      <ul className="list-disc pl-5 mt-2 flex flex-col gap-1 leading-relaxed">
        {(defs.length ? defs : ENG_DEFAULT_DEFS).map((d, i) => <li key={i}>{d}</li>)}
      </ul>
    </details>
  );
};

/** "339 · 58% of opened": an automated count with its share. */
export const shareHint = (share: any, of: string): string | undefined =>
  (share === null || share === undefined || !Number.isFinite(Number(share)) ? undefined
    : `${pctText(Number(share))} of ${of}`);

// ---------------------------------------------------------------------------
// Change vs the previous period (monthly.Delta's rule, plus "lower is better"
// for bounces and unsubscribes, which Delta would colour green on a rise)
// ---------------------------------------------------------------------------
export const Change: React.FC<{
  cur: number | null | undefined; prev: number | null | undefined; kind: 'count' | 'rate';
  vs?: string; lowerIsBetter?: boolean;
}> = ({ cur, prev, kind, vs, lowerIsBetter }) => {
  if (cur === null || cur === undefined || prev === null || prev === undefined) return null;
  let text: string; let dir: number;
  if (kind === 'rate') {
    const pp = (cur - prev) * 100;
    dir = Math.abs(pp) < 0.05 ? 0 : Math.sign(pp);
    text = `${pp > 0 ? '+' : ''}${pp.toFixed(1)} pp`;
  } else if (prev === 0) {
    if (cur === 0) return null;
    dir = 1; text = 'new';
  } else {
    const d = ((cur - prev) / prev) * 100;
    dir = Math.round(d) === 0 ? 0 : Math.sign(d);
    text = `${d > 0 ? '+' : ''}${Math.abs(d) < 10 ? d.toFixed(1) : Math.round(d)}%`;
  }
  const good = lowerIsBetter ? -dir : dir;
  const look = good > 0 ? 'text-green-700' : good < 0 ? 'text-red-600' : 'text-grayscale-1100';
  return (
    <span className={`text-[10px] whitespace-nowrap ${look}`} title={vs ? `vs ${vs}` : undefined}>
      {dir > 0 ? '▲' : dir < 0 ? '▼' : '='} {text}
    </span>
  );
};

// ---------------------------------------------------------------------------
// A column per week, with a metric switch (monthly.MonthTrend's chart, keyed
// by week instead of month). The running week is paler and marked *.
// ---------------------------------------------------------------------------
export type WeekMetric = {
  key: string; label: string; kind: 'count' | 'rate';
  series?: { key: string; label: string; tone: string }[];
  note?: string;
};
export type WeekPoint = Record<string, any> & { week: string; partial?: boolean };

export const WeekTrend: React.FC<{
  rows: WeekPoint[]; metrics: WeekMetric[]; selected?: string; onSelect?: (w: string) => void; initial?: string;
}> = ({ rows, metrics, selected, onSelect, initial }) => {
  const [metric, setMetric] = useState(initial || metrics[0]?.key || '');
  const spec = metrics.find(m => m.key === metric) || metrics[0];
  if (!spec) return null;
  const series = spec.series?.length ? spec.series : [{ key: spec.key, label: spec.label, tone: 'bg-primary-500' }];
  const val = (r: any, k: string): number | null => {
    const v = r[k];
    return v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v);
  };
  const max = Math.max(spec.kind === 'rate' ? 0.0001 : 1, ...rows.flatMap(r => series.map(s => val(r, s.key) ?? 0)));
  const show = (v: number | null) => (spec.kind === 'rate' ? (v === null ? 'n/a' : pctText(v)) : fmtN(v));
  const many = rows.length > 16;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-1 flex-wrap">
        {metrics.map(m => (
          <button key={m.key} type="button" onClick={() => setMetric(m.key)}
            className={`py-1 px-2.5 rounded-full text-xs font-grotesk whitespace-nowrap ${spec.key === m.key
              ? 'bg-primary-500 text-grayscale-100 font-bold'
              : 'border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200'}`}>
            {m.label}
          </button>
        ))}
      </div>
      {(series.length > 1 || spec.note) && (
        <div className="flex flex-wrap gap-3 text-[11px] text-grayscale-1100">
          {series.length > 1 && series.map(s => (
            <span key={s.key}><span className={`inline-block w-2 h-2 rounded-sm mr-1 ${s.tone}`} />{s.label}</span>
          ))}
          {spec.note && <span>{spec.note}</span>}
        </div>
      )}
      {!rows.length ? <div className="text-sm text-grayscale-1100 py-6 text-center">Nothing sent in this period.</div> : (
        <div className="flex gap-2">
          <div className="flex flex-col justify-between text-[10px] text-grayscale-1100 text-right w-12 shrink-0 h-44 pt-4">
            <span>{show(max)}</span><span>0</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="relative h-44 border-b border-grayscale-200">
              <div aria-hidden className="absolute inset-x-0 top-4 border-t border-dashed border-grayscale-200" />
              <div className={`absolute inset-0 flex items-end ${many ? 'gap-0.5' : 'gap-2'}`}>
                {rows.map(r => (
                  <button key={r.week} type="button" onClick={onSelect ? () => onSelect(r.week) : undefined}
                    title={`${weekLabel(r.week)}${r.partial ? ' (part week)' : ''}: ${series.map(s => `${s.label} ${show(val(r, s.key))}`).join(' · ')}`}
                    className={`flex-1 min-w-0 h-full flex items-end justify-center gap-[2px] px-0.5 rounded-t ${
                      r.week === selected ? 'bg-primary-100' : 'hover:bg-grayscale-200/60'} ${onSelect ? '' : 'cursor-default'}`}>
                    {series.map(s => {
                      const v = val(r, s.key);
                      return (
                        <div key={s.key} className="flex-1 max-w-[2.5rem] h-full flex flex-col justify-end items-center">
                          {!many && (series.length === 1 || v) ? (
                            <span className="text-[10px] text-grayscale-1200 leading-tight mb-0.5">{show(v)}</span>
                          ) : null}
                          <div className={`w-full rounded-t ${s.tone} ${r.partial ? 'opacity-60' : ''}`}
                            style={{ height: v ? `max(2px, calc((100% - 1rem) * ${v / max}))` : 0 }} />
                        </div>
                      );
                    })}
                  </button>
                ))}
              </div>
            </div>
            <div className={`flex text-[10px] text-grayscale-1100 mt-1 ${many ? 'gap-0.5' : 'gap-2'}`}>
              {rows.map((r, i) => (
                <span key={r.week} className={`flex-1 min-w-0 text-center truncate ${r.week === selected ? 'font-bold text-grayscale-1300' : ''}`}>
                  {many && i % Math.ceil(rows.length / 12) !== 0 ? '' : `${dm(r.week)}${r.partial ? '*' : ''}`}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
      {rows.some(r => r.partial) && (
        <div className="text-[10px] text-grayscale-1100">
          Each column is a week starting Monday (India time). * part of a week only — the running week, or a
          week cut by the period (paler column).
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Excel export. The admin dispatcher wraps every reply in JSON, so the
// workbook comes back base64 (the University contacts export does the same);
// a reply that names a file path instead is fetched from that admin route.
// ---------------------------------------------------------------------------
const XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
export async function downloadStatsExport(params: Record<string, string | number>):
  Promise<{ error: string; info: string }> {
  const { data, error } = await panelGet(SECTION, 'stats_export', params);
  if (error) return { error, info: '' };
  const d: any = data || {};
  const ymd = istToday().replace(/-/g, '');
  const sheets = arr(d.sheets).map((s: any) => `${str(s?.sheet)} ${num(s?.rows).toLocaleString()}`).join(', ');
  const info = [str(d.filename), sheets && `(${sheets})`,
    d.truncated ? '— the Sends sheet is cut short; narrow the period for every send.' : ''].filter(Boolean).join(' ');
  const b64 = str(d.content_b64 || d.b64 || d.base64);
  if (b64) {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    saveBlob(new Blob([bytes], { type: str(d.content_type) || XLSX }), str(d.filename) || `email-stats-${ymd}.xlsx`);
    return { error: '', info };
  }
  const path = str(d.download_path || d.path || d.url).replace(/^https?:\/\/[^/]+/i, '');
  if (path.startsWith('/')) {
    const r = await apiService.adminFetchFile(apiService.getStoredUserId() || '', path);
    if (r.error || !r.blob) return { error: r.error || 'Download failed', info: '' };
    saveBlob(r.blob, r.filename || str(d.filename) || `email-stats-${ymd}.xlsx`);
    return { error: '', info };
  }
  return { error: 'The server sent no file back.', info: '' };
}
